import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from '@/components/ui/button';
import { ChannelIcon } from '@/components/ui/channel-icon';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useEditCampaignContext } from '@/contexts/edit-campaign-context';
import { POOL } from '@/types/channels';
import { getChannelName } from '@/utils/channel-names';

import { useBudgetEditStart } from '../use-budget-edit-start';

import { ChannelSwitcher } from './channel-switcher';
import { EditWarningBanner } from './edit-warning-banner';

interface OtherChannelsDialogProps {
  channelSlug: string;
}

export function OtherChannelsDialog({ channelSlug }: OtherChannelsDialogProps) {
  const {
    campaign,
    closeForm,
    hookForm,
    onFormSubmit,
    isSubmitting,
    isFormDirty,
    isCompleted,
    isFetching,
  } = useEditCampaignContext();
  const isReadonly = campaign?.is_readonly;
  const isTargetMetricsEditable =
    campaign?.permissions.is_target_metrics_editable;
  const channelDetails = campaign?.sourcing_channel;

  const { reset: resetForm, handleSubmit } = hookForm;
  const isPool = channelSlug === POOL;

  useEffect(() => {
    if (campaign) {
      resetForm({
        starts_at: campaign.starts_at,
        end_at: campaign.end_at ?? null,
        budget: campaign.budget || null,
        budget_type: campaign.budget_type ?? 'lifetime',
        end_at_type: campaign.end_at_type ?? 'fixed',
        expected_cpa: campaign.expected_cpa || null,
        target_applicants: null,
        expected_conversion: campaign.expected_conversion || null,
        target_hires: null,
      });
    }
  }, [campaign, resetForm, channelSlug]);
  useBudgetEditStart();

  return (
    <Dialog open={!!channelSlug} onOpenChange={() => closeForm()}>
      <DialogContent className="min-w-[640px]">
        <DialogHeader>
          <DialogTitle>
            {isCompleted || isReadonly ? (
              <FormattedMessage
                defaultMessage="Campaign details"
                description="Read-only modal title when campaign cannot be edited"
              />
            ) : (
              <FormattedMessage
                defaultMessage="Edit Campaign"
                description="Modal title for changing a non-Reach channel campaign"
              />
            )}
          </DialogTitle>
        </DialogHeader>

        <form
          className="flex min-h-0 flex-1 flex-col"
          onSubmit={handleSubmit(onFormSubmit)}
        >
          <DialogBody className="flex flex-col gap-6 p-6">
            {isFetching ? (
              <ChannelIcon.Skeleton
                variant="large"
                size="lg"
                className="mx-auto"
              />
            ) : (
              <ChannelIcon
                channelSlug={channelSlug}
                variant="large"
                size="lg"
                className="mx-auto max-w-full"
                channelName={
                  channelDetails?.name ?? getChannelName(channelSlug)
                }
                logoSquareUrl={channelDetails?.logo_square}
                logoRectangleUrl={channelDetails?.logo_rectangle}
              />
            )}
            {isReadonly && !isPool && <EditWarningBanner />}
            <ChannelSwitcher channelSlug={channelSlug} />
          </DialogBody>

          <DialogFooter>
            {!isCompleted &&
              (!isReadonly || isTargetMetricsEditable) &&
              !isPool && (
                <Button type="submit" disabled={!isFormDirty || isSubmitting}>
                  {isSubmitting && <Loader2 className="animate-spin" />}
                  <FormattedMessage
                    defaultMessage="Update campaign"
                    description="Submit changes for this channel’s budget and schedule"
                  />
                </Button>
              )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
